import { useLocalDatabase } from 'hooks/useLocalDatabase';
import { LocalTrack } from 'models/LocalTrack';
import { useRef, useState } from 'react';

const allowedFileExtensions = [
  '.aif',
  '.aifc',
  '.aiff',
  '.flac',
  '.3gp',
  '.m4a',
  '.m4b',
  '.m4p',
  '.m4r',
  '.m4v',
  '.mp4',
  '.ape',
  '.asf',
  '.wma',
  '.wmv',
  '.mp2',
  '.mp3',
  '.ogg',
  '.wav',
];

export const useMusicLibraryImport = () => {
  const [importedCount, setImportedCount] = useState(0);
  const [readCount, setReadCount] = useState(0);
  const [importing, setImporting] = useState(false);
  const [failedFiles, setFailedFiles] = useState<string[]>([]);
  const { localDatabase } = useLocalDatabase();
  const abortController = useRef<AbortController | null>(null);

  const importFolderRecursive = async (folderPath: string) => {
    const pathIsFolder = (await window.electron?.isDirectory(folderPath)) ?? false;
    const dirContent = pathIsFolder
      ? await window.electron?.readDirRecursive(folderPath, abortController.current?.signal)
      : [folderPath];

    if (dirContent) {
      for (let i = 0; i < dirContent.length; i++) {
        if (abortController.current?.signal.aborted) {
          return;
        }

        const fileName = dirContent[i];
        let fullPath = pathIsFolder ? window.electron?.joinPath(folderPath, fileName) : fileName;

        if (fullPath == undefined) {
          continue;
        }

        const isDirectory = await window.electron?.isDirectory(fullPath);

        if (isDirectory) {
          // await importFolderRecursive(fullPath);
          continue;
        }

        const fileExtension = window.electron?.getFileExtension(fileName);
        setReadCount((prevCount) => prevCount + 1);

        if (fileExtension && allowedFileExtensions.includes(fileExtension)) {
          try {
            let fileMetadata: any = null;
            try {
              fileMetadata = await window.electron?.parseMetaData(fullPath);
            } catch (metadataError) {
              window.electron?.logWarn(`Metadata parsing failed for ${fullPath}, using fallback. Error: ${metadataError}`);
            }

            // Extract the filename (basename) without extension for the fallback title
            const lastSlashIndex = Math.max(fullPath.lastIndexOf('/'), fullPath.lastIndexOf('\\'));
            let filenameWithoutExt = lastSlashIndex !== -1 ? fullPath.substring(lastSlashIndex + 1) : fullPath;
            if (fileExtension && filenameWithoutExt.endsWith(fileExtension)) {
              filenameWithoutExt = filenameWithoutExt.substring(0, filenameWithoutExt.length - fileExtension.length);
            }

            const rawArtist = fileMetadata?.common?.artist?.trim();
            const rawTitle = fileMetadata?.common?.title?.trim();

            const useFallback = !rawArtist && !rawTitle;

            const song = {
              artist: useFallback ? '' : (rawArtist || ''),
              title: useFallback ? filenameWithoutExt : (rawTitle || filenameWithoutExt),
              genre: Array.isArray(fileMetadata?.common?.genre)
                ? fileMetadata.common.genre.join(',')
                : (fileMetadata?.common?.genre || ''),
              bpm: fileMetadata?.common?.bpm ?? 0,
              key: fileMetadata?.common?.key ?? '',
              path: fullPath,
            } as LocalTrack;

            var tmpExists = await localDatabase?.pathExists(song.path);
            console.log('tmpExists', tmpExists);
            //if (!localDatabase?.pathExists(song.path)) {
            if(!tmpExists){
              await localDatabase?.addTrack(song);
              setImportedCount((prevCount) => prevCount + 1);
            }
          } catch (error) {
            setFailedFiles((prevFailedFiles) =>
              fullPath ? [...prevFailedFiles, fullPath] : prevFailedFiles
            );
            window.electron?.logWarn(`Import failed for ${fullPath}, ${error}`);
            continue;
          }
        }
      }
    }
    localDatabase?.reorgMapping();
  };

  const handleImport = async (path: string) => {
    setImporting(true);
    // localDatabase?.createTables();
    setReadCount(0);
    setImportedCount(0);
    abortController.current = new AbortController();

    try {
      await importFolderRecursive(path);
      localDatabase?.reorgMapping();
      setImporting(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAbort = () => {
    if (abortController.current) {
      abortController.current?.abort();
      setImporting(false);
    }
  };

  const countFilesRecursive = (dir: string) => {
    return window.electron?.countFilesRecursive(dir);
  };

  const resetDatabase = () => {
    localDatabase?.resetDatabase();
  };

  return {
    handleAbort,
    handleImport,
    importedCount,
    importing,
    readCount,
    resetDatabase,
    countFilesRecursive,
    failedFiles,
  };
};

export default useMusicLibraryImport;
